import React, { useEffect, useState, useRef } from 'react';
import { Box, IconButton, InputAdornment, Typography, Button } from '@mui/material';
import StyledGrid, { StyledTextSearch, FilterChip } from '../../../shared/components/StyledGrid';
import { useGetPremiumsMutation } from '../../../services/InsuranceService';
import useFetchWithMsal from '../../../utils/useFetchWithMsal';
import { useSelector, useDispatch } from 'react-redux';
import { premiumsColumns } from '../../../constants/TablesColumns';
import { LoadingPanel } from '../../../shared/components/LoadingPanel';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { setNaicCode, setMemberName } from '../../../store/slices/FilterSlice';
import ExportToExcelButton from '../../../shared/components/ExportToExcelButton';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

export const PremiumsTab = () => {
  const dispatch = useDispatch();
  const { skip } = useFetchWithMsal();
  const { naicCode, memberName } = useSelector(state => state.filter);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
  const [sortingModel, setSortingModel] = useState();
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState(naicCode || memberName || '');
  const [debounceTimer, setDebounceTimer] = useState(null);
  const fileInputRef = useRef();

  const [getPremiums, { data, isLoading, error }] = useGetPremiumsMutation('', { skip });

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    const file = files[0];
    // Placeholder: implement parsing later
    /* eslint-disable no-console */
    console.debug('[PremiumsTab] Import selected', { fileName: file.name, size: file.size });
    /* eslint-enable no-console */
  };

  const fetchData = (override) => {
    const body = {
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      orderBy: sortingModel?.field || '',
      orderByDirection: sortingModel?.sort || '',
      naicCode: override ? override.naicCode : naicCode,
      memberName: override ? override.memberName : memberName,
      processingYear: override ? override.processingYear : null,
    };
    getPremiums(body);
  };

  useEffect(() => {
    if (!skip) fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize, sortingModel?.field, sortingModel?.sort, skip]);

  // Debounced unified search logic
  useEffect(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      const trimmed = searchTerm.trim();
      if (!trimmed) {
        dispatch(setNaicCode(null));
        dispatch(setMemberName(null));
        fetchData({ naicCode: null, memberName: null });
        return;
      }
      const isDigits = /^\d+$/.test(trimmed);
      const naicVal = isDigits ? trimmed : null;
      const memberVal = isDigits ? null : trimmed;
      dispatch(setNaicCode(naicVal));
      dispatch(setMemberName(memberVal));
      fetchData({ naicCode: naicVal, memberName: memberVal });
    }, 400);
    setDebounceTimer(timer);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (!isLoading && data?.totalCount !== undefined) setTotalCount(data.totalCount);
  }, [isLoading, data?.totalCount]);

  const handlePaginationModelChange = (model) => setPaginationModel(model);

  return (
    <Box sx={{ height: '100%', width: '100%', margin: '50px auto 0 auto' }}>
      {isLoading && <LoadingPanel />}
      <Box display='flex' sx={{ justifyContent: 'space-between' }} mb={3}>
        <Typography variant='h1' color='primary.main' sx={{ marginLeft: '16px' }}>
          Premiums
        </Typography>
        <Box display='flex' gap={2} alignItems='center'>
          <Button
            variant='outlined'
            startIcon={<CloudUploadIcon fontSize='small' />}
            onClick={handleImportClick}
          >
            Import
          </Button>
          <ExportToExcelButton
            rows={data?.premiums || []}
            columns={premiumsColumns}
            filename='premiums.xlsx'
            label='Export'
          />
          <input
            ref={fileInputRef}
            type='file'
            style={{ display: 'none' }}
            accept={'.csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
            onChange={handleFileSelected}
          />
        </Box>
      </Box>
      <Box mb={1} sx={{ display: 'flex', justifyContent: 'left', flexWrap: 'wrap', margin: '10px 0 5px 0', gap: 1 }}>
        <Box mb={2} sx={{ display: 'flex', maxWidth: 400, alignItems: 'center' }}>
          <StyledTextSearch
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position='end'>
                  <IconButton size='small' onClick={() => setSearchTerm('')}>
                    <CloseIcon fontSize='inherit' />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            placeholder='Type NAIC code or Member name'
          />
        </Box>
        {naicCode && (
          <FilterChip>
            NAIC Code: {naicCode}
            <IconButton size='small' onClick={() => { dispatch(setNaicCode(null)); dispatch(setMemberName(null)); setSearchTerm(''); fetchData({ naicCode: null, memberName: null }); }}>
              <CloseIcon fontSize='inherit' />
            </IconButton>
          </FilterChip>
        )}
        {memberName && (
          <FilterChip>
            Member Name: {memberName}
            <IconButton size='small' onClick={() => { dispatch(setNaicCode(null)); dispatch(setMemberName(null)); setSearchTerm(''); fetchData({ naicCode: null, memberName: null }); }}>
              <CloseIcon fontSize='inherit' />
            </IconButton>
          </FilterChip>
        )}
      </Box>
      <StyledGrid
        data={data?.premiums || []}
        width='100%'
        columns={premiumsColumns}
        rowCount={totalCount}
        onSortingModelChange={(model) => setSortingModel(model[0])}
        paginationModel={paginationModel}
        setPaginationModel={handlePaginationModelChange}
      />
    </Box>
  );
};

export default PremiumsTab;
